import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { createFixVersion, updateFixVersion } from '../../services/apiService';

/**
 * ReleaseEditDialog component for creating or editing a release
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when the dialog is closed
 * @param {Object} props.release - Release to edit (null for create)
 * @param {Function} props.onSave - Function to call when the release is saved
 * @returns {JSX.Element} ReleaseEditDialog component
 */
const ReleaseEditDialog = ({ open, onClose, release, onSave }) => {
  // State for form fields
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [releaseDate, setReleaseDate] = useState(null);
  const [adoTestPlanFolderId, setAdoTestPlanFolderId] = useState('');
  const [active, setActive] = useState(true);
  const [isMaintenanceRelease, setIsMaintenanceRelease] = useState(false);
  
  // State for loading and error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Initialize form fields when release changes
  useEffect(() => {
    if (release) {
      setVersion(release.version || '');
      setDescription(release.description || '');
      setReleaseDate(release.releaseDate ? dayjs(release.releaseDate) : null);
      setAdoTestPlanFolderId(release.adoTestPlanFolderId || '');
      setActive(release.active !== undefined ? release.active : true);
      setIsMaintenanceRelease(release.isMaintenanceRelease || false);
    } else {
      // Reset form fields for create
      setVersion('');
      setDescription('');
      setReleaseDate(null);
      setAdoTestPlanFolderId('');
      setActive(true);
      setIsMaintenanceRelease(false);
    }
  }, [release]);
  
  // Handle save
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate required fields
      if (!version) {
        setError('Version is required');
        return;
      }
      
      // Create release object
      const releaseData = {
        version,
        description,
        releaseDate,
        adoTestPlanFolderId,
        active,
        isMaintenanceRelease
      };
      
      let response;
      
      if (release && release._id) {
        // Update existing release
        response = await updateFixVersion(release._id, releaseData);
      } else {
        // Create new release
        response = await createFixVersion(releaseData);
      }
      
      if (response && (response.success || response.data)) {
        // Call onSave with the saved release
        onSave(response.data || response);
        onClose();
      } else {
        throw new Error('Failed to save release');
      }
    } catch (error) {
      console.error('Error saving release:', error);
      setError(error.message || 'Failed to save release. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {release && release._id ? 'Edit Release' : 'Create New Release'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              fullWidth
              required
              margin="normal"
              helperText="Format: 5.9.08"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Release Date"
                value={releaseDate}
                onChange={(newValue) => setReleaseDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal"
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="ADO Test Plan Folder ID"
              value={adoTestPlanFolderId}
              onChange={(e) => setAdoTestPlanFolderId(e.target.value)}
              fullWidth
              margin="normal"
              helperText="ID of the test plan folder in Azure DevOps"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
            
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseEditDialog;
